/**
 * The single source of plan prices, billing periods and discount rates (ADR-010).
 *
 * Amounts are integers in hundredths of a leone ("minor units": SLE 212.50 is 21250), so no
 * price is ever held in floating point. Discount rates are integer basis points (1500 = 15%).
 * Plan IDs are stable internal identifiers; display names may change without breaking code.
 */

export const PLAN_IDS = ['student', 'professional', 'firm'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export type PlanPricing = {
  id: PlanId;
  /** Display name. */
  name: string;
  /** Undiscounted monthly base price, in minor units, per account or per person. */
  monthlyBaseMinor: number;
  /** True when the price is per person (seat count applies). */
  perPerson: boolean;
};

export const PLANS: Readonly<Record<PlanId, PlanPricing>> = {
  student: { id: 'student', name: 'Student', monthlyBaseMinor: 25_000, perPerson: false },
  professional: { id: 'professional', name: 'Lawyer', monthlyBaseMinor: 45_000, perPerson: false },
  firm: { id: 'firm', name: 'Law Firm', monthlyBaseMinor: 60_000, perPerson: true },
};

/** Display order of the selector: annual, quarterly, monthly. There is no semi-annual option. */
export const BILLING_PERIOD_IDS = ['annual', 'quarterly', 'monthly'] as const;
export type BillingPeriodId = (typeof BILLING_PERIOD_IDS)[number];

export type BillingPeriod = {
  id: BillingPeriodId;
  /** Months paid upfront. */
  months: number;
  /** Discount on the undiscounted monthly base, in basis points. Never stacked. */
  discountBasisPoints: number;
  /** Selector text, savings included. */
  label: string;
  /** Used in "billed …" sentences. */
  frequency: string;
};

export const BILLING_PERIODS: Readonly<Record<BillingPeriodId, BillingPeriod>> = {
  annual: {
    id: 'annual',
    months: 12,
    discountBasisPoints: 1_500,
    label: 'Pay Annually — Save 15%',
    frequency: 'annually',
  },
  quarterly: {
    id: 'quarterly',
    months: 3,
    discountBasisPoints: 1_000,
    label: 'Pay Quarterly — Save 10%',
    frequency: 'every 3 months',
  },
  monthly: {
    id: 'monthly',
    months: 1,
    discountBasisPoints: 0,
    label: 'Pay Monthly',
    frequency: 'monthly',
  },
};

export const DEFAULT_BILLING_PERIOD: BillingPeriodId = 'monthly';
